import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom"
import useLocalStorageState from "use-local-storage-state"
import { useEffect } from "react";

import { askNotificationPermission, sendNotification } from './Utils/Notification'; import { formatDate, formatTime, RefreshPermission } from "./Utils/helpers/commonhelper";
import { GetRequest, UpdateRF } from "./Utils/hooks/ApiHandler";

const AUTH_PATHS = new Set([
    "/",
    "/login",
    "/register",
    "/forgot-password",
    "/registrationSuccessful",
]);

export const Protected = () => {
    const [customer, setCustomer] = useLocalStorageState('customer')
    // rbac permission object
    const [_, setPermission] = useLocalStorageState('permission')

    const navigate = useNavigate();
    const location = useLocation();
    // Sibling AuthRoutes owns these URLs; Routers' catch-all must not session-gate them.
    const isAuthPath =
        AUTH_PATHS.has(location.pathname) ||
        location.pathname.startsWith("/create-password/");

    const customerId = customer?.customer_details?.customer_id;
    const LoginUserId = customer?.customer_details?.id;
    const user_type = customer?.customer_details?.user_type;
    const token = customer?.token?.authorization;
    const rf = customer?.token?.authentication;
    const headers = {
        authentication: rf,
        Authorization: token
    }
    const extras = { customer, setCustomer, rf, navigate };

    const logout = () => {
        UpdateRF(customer?.token?.authentication);

        setCustomer({
            customer_details: {},
            customer_status: "Inactive",
            user_login: false,
            token: {},
        });

        localStorage.clear();
        navigate('/login', { replace: true });
    };

    useEffect(() => {
        if (isAuthPath) {
            return;
        }
        if (!customer || !customerId || !LoginUserId) {
            logout();
            return;
        }
        RefreshPermission(customerId, LoginUserId, headers, extras, setCustomer, setPermission, user_type)
        const fetchNotification = async () => {
            try {
                await Promise.all([
                    fetchDataForTaskBeforeDueTime(),
                    fetchDataForTaskOnTime(),
                    fetchDataForMettingBeforeDueTime(),
                    fetchDataForMettingOnTime(),
                    fetchDataTodoDueNow(),
                    fetchDataTodoReminderDue(),
                ]);
            } catch (error) {
                console.error("Notification fetch error:", error);
            }
        };
        const interval = setInterval(() => {
            fetchNotification();
        }, 60000);
        return () => clearInterval(interval);
    }, [isAuthPath]);

    if (isAuthPath) {
        return null;
    }

    if (
        !customer ||
        customer?.user_login === undefined ||
        customer?.customer_status === undefined ||
        customer?.user_login === false ||
        customer?.customer_status === "Inactive"
    ) {
        localStorage.clear();
        return <Navigate to="/login" replace />;
    }
    const isFree = customer.customer_details?.is_plan === "no" ? false : true
    if (!isFree) {
        return <Navigate to={"/plans"} replace />
    }

    //  API Call + Notifications
    const fetchDataForTaskBeforeDueTime = async () => {
        await askNotificationPermission();

        try {
            const response = await GetRequest(
                `/Notification/taskNotificationBeforeDueTime/${LoginUserId}/${customerId}`,
                { headers },
                extras
            );
            if (response.status === "fail" || response.success === false) {
                return
            }
            response?.data && response?.data?.forEach((task, index) => {
                setTimeout(() => {
                    sendNotification(
                        `Task: ${task?.name} (${task.priority})`,
                        `${task?.description}\nDue: ${formatDate(task?.due_date)} at ${formatTime(task?.due_time)}`
                    );
                }, index * 3000);
            });
        } catch (error) {
            console.error("Error fetching API:", error);
            return
        }
    };

    const fetchDataForTaskOnTime = async () => {
        await askNotificationPermission();
        try {
            const response = await GetRequest(
                `/Notification/taskNotificationOnTime/${LoginUserId}/${customerId}`,
                { headers },
                extras
            );

            if (response.status === "fail" || response.success === false) {
                return
            }
            response && response.forEach((task, index) => {
                setTimeout(() => {
                    sendNotification(
                        `Task: ${task.name} (${task.priority})`,
                        `${task.description}\nDue: ${task.due_date} at ${task.due_time}`
                    );
                }, index * 3000);
            });
        } catch (error) {
            console.error("Error fetching API:", error);
            return
        }
    }

    const fetchDataForMettingBeforeDueTime = async () => {
        await askNotificationPermission();

        try {
            const response = await GetRequest(
                `/Notification/fetchMeetingBeforeDueTime/${LoginUserId}/${customerId}`,
                { headers },
                extras
            );

            if (response.status === "fail" || response.success === false) {
                return
            }
            response?.data && response?.data?.forEach((task, index) => {
                setTimeout(() => {
                    sendNotification(
                        `Task: ${task.meeting_title} (${task.meeting_type})`,
                        `${task.description}\nDue: ${task.meeting_date} at ${task.meeting_from}`
                    );
                }, index * 3000);
            });
        } catch (error) {
            console.error("Error fetching API:", error);
            return
        }
    }
    const fetchDataForMettingOnTime = async () => {
        await askNotificationPermission();

        try {
            const response = await GetRequest(
                `/Notification/fetchMeetingOnTime/${LoginUserId}/${customerId}`,
                { headers },
                extras
            );

            if (response.status === "fail" || response.success === false) {
                return
            }
            response?.data && response?.data?.forEach((task, index) => {
                setTimeout(() => {
                    sendNotification(
                        `Task: ${task.meeting_title} (${task.meeting_type})`,
                        `${task.description}\nDue: ${task.meeting_date} at ${task.meeting_from}`
                    );
                }, index * 3000);
            });
        } catch (error) {
            console.error("Error fetching API:", error);
            return
        }
    }

    const fetchDataTodoDueNow = async () => {
        await askNotificationPermission();

        try {
            const response = await GetRequest(
                `/Notification/todo_due_now/${LoginUserId}/${customerId}`,
                { headers },
                extras
            );

            if (response.status === "fail" || response.success === false) {
                return
            }
            response?.data && response?.data?.forEach((task, index) => {
                setTimeout(() => {
                    sendNotification(
                        `Todo: ${task?.title}`,

                    );
                }, index * 3000);
            });
        } catch (error) {
            console.error("Error fetching API:", error);
            return
        }
    }
    const fetchDataTodoReminderDue = async () => {
        await askNotificationPermission();

        try {
            const response = await GetRequest(
                `/Notification/todo_reminder_due/${LoginUserId}/${customerId}`,
                { headers },
                extras
            );

            if (response.status === "fail" || response.success === false) {
                return
            }
            response?.data && response?.data?.forEach((task, index) => {
                setTimeout(() => {
                    sendNotification(
                        `Todo: ${task?.title}`,

                    );
                }, index * 3000);
            });
        } catch (error) {
            console.error("Error fetching API:", error);
            return
        }
    }


    return <Outlet />;
}